<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { AdminDataTable, AdminFilterBar, AdminStatusTag, AdminDrawer, RrInput, RrSelect, RrButton, RrForm, RrFormItem, RrCheckbox, RrTag, RrRadioGroup, RrSpace, type TableColumnType } from '@xjlv2/admin-layout'
import { ArrowLeft, Download, FileUp, Plus, Search, ShieldAlert, Trash2, X } from '@lucide/vue'
import type {
  MachineTag,
  ReviewLexicon,
  ReviewTerm,
  SaveLexiconInput,
  TermType
} from './types'
import {
  LEXICON_RESULT_OPTIONS,
  MACHINE_TAG_OPTIONS,
  MATCH_MODE_OPTIONS,
  TERM_ACCOUNT_LIMIT,
  TERM_BATCH_LIMIT,
  TERM_TYPE_OPTIONS,
  lexiconCategoryLabel,
  lexiconResultLabel,
  machineTagLabel,
  matchModeLabel,
  termTypeLabel
} from './types'
import { lexiconResultClass, machineTagTone } from './format'
import {
  addTerms,
  createLexicon,
  deleteLexicon,
  deleteTerms,
  listLexicons,
  listTerms,
  updateLexicon
} from './mock'
import { usePaged } from './usePaged'
import ContentReviewPager from './ContentReviewPager.vue'

const props = defineProps<{
  canManage: boolean
}>()

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
}>()

const loading = ref(false)
const submitting = ref(false)

// —— 词库列表 ——
const lexicons = ref<ReviewLexicon[]>([])
const keyword = ref('')
const listPaged = usePaged(() => lexicons.value, 10)

const lexiconColumns: TableColumnType<ReviewLexicon>[] = [
  { key: 'name', title: '词库名称', minWidth: 200 },
  { key: 'result', title: '识别结果', width: 120 },
  { key: 'matchMode', title: '匹配模式', width: 120 },
  { key: 'updatedAt', title: '库信息修改时间', width: 180 },
  { key: 'actions', title: '操作', width: 180, align: 'right', fixed: 'right' }
]

const termColumns: TableColumnType<ReviewTerm>[] = [
  { key: 'term', title: '关键词', minWidth: 150 },
  { key: 'id', title: '关键词ID', width: 100 },
  { key: 'machineTags', title: '机审标签', minWidth: 200 },
  { key: 'type', title: '关键词类型', width: 100 },
  { key: 'remark', title: '备注', minWidth: 150 },
  { key: 'createdAt', title: '创建时间', width: 180 },
  { key: 'actions', title: '操作', width: 100, align: 'right', fixed: 'right' }
]

// To handle selection in RrTable/AdminDataTable
const handleSelectionChange = (keys: (string | number)[]) => {
  selectedTermIds.value = keys as number[]
}


async function refreshLexicons() {
  loading.value = true
  emit('error', '')
  try {
    lexicons.value = await listLexicons(keyword.value)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '词库加载失败')
  } finally {
    loading.value = false
  }
}

// —— 词库详情（关键词管理）——
const mode = ref<'list' | 'detail'>('list')
const activeLexicon = ref<ReviewLexicon | null>(null)
const terms = ref<ReviewTerm[]>([])
const termKeyword = ref('')
const termTag = ref<MachineTag | ''>('')
const selectedTermIds = ref<number[]>([])
const termPaged = usePaged(() => terms.value, 10)
const accountTermCount = computed(() => lexicons.value.reduce((sum, item) => sum + item.termCount, 0))
const allTermsSelected = computed(() => termPaged.paged.value.length > 0
  && termPaged.paged.value.every((item) => selectedTermIds.value.includes(item.id)))

async function openDetail(lexicon: ReviewLexicon) {
  activeLexicon.value = lexicon
  mode.value = 'detail'
  void loadTerms()
}

async function loadTerms() {
  if (!activeLexicon.value) return
  loading.value = true
  emit('error', '')
  try {
    terms.value = await listTerms(activeLexicon.value.id, termKeyword.value, termTag.value)
    selectedTermIds.value = []
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '关键词加载失败')
  } finally {
    loading.value = false
  }
}

function backToList() {
  mode.value = 'list'
  activeLexicon.value = null
  void refreshLexicons()
}

// —— 新建 / 编辑词库 ——
const showLexiconDialog = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const editingLexicon = ref<ReviewLexicon | null>(null)
const lexiconForm = ref<SaveLexiconInput>({ name: '', result: 'VIOLATION', matchMode: 'FUZZY', category: 'CUSTOM' })

function openCreateDialog() {
  dialogMode.value = 'create'
  editingLexicon.value = null
  lexiconForm.value = { name: '', result: 'VIOLATION', matchMode: 'FUZZY', category: 'CUSTOM' }
  showLexiconDialog.value = true
}

function openEditDialog(lexicon: ReviewLexicon) {
  dialogMode.value = 'edit'
  editingLexicon.value = lexicon
  lexiconForm.value = {
    name: lexicon.name,
    result: lexicon.result,
    matchMode: lexicon.matchMode,
    category: lexicon.category
  }
  showLexiconDialog.value = true
}

async function submitLexicon() {
  if (!lexiconForm.value.name.trim()) {
    emit('error', '请输入词库名称。')
    return
  }
  submitting.value = true
  try {
    if (dialogMode.value === 'create') {
      await createLexicon(lexiconForm.value)
    } else if (editingLexicon.value) {
      await updateLexicon(editingLexicon.value.id, lexiconForm.value)
    }
    showLexiconDialog.value = false
    emit('notice', dialogMode.value === 'create' ? '词库已创建。' : '词库信息已保存。')
    await refreshLexicons()
    if (activeLexicon.value) {
      activeLexicon.value = lexicons.value.find((item) => item.id === activeLexicon.value?.id) ?? activeLexicon.value
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '词库保存失败')
  } finally {
    submitting.value = false
  }
}

async function removeLexicon(lexicon: ReviewLexicon) {
  if (!window.confirm(`确定删除词库「${lexicon.name}」吗？将同时删除词库内全部关键词。`)) return
  loading.value = true
  try {
    await deleteLexicon(lexicon.id)
    emit('notice', '词库已删除。')
    await refreshLexicons()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '词库删除失败')
  } finally {
    loading.value = false
  }
}

async function exportLexicon(lexicon: ReviewLexicon) {
  try {
    const rows = await listTerms(lexicon.id)
    const header = '关键词,关键词ID,机审标签,关键词类型,备注,创建时间'
    const lines = rows.map((row) => [
      row.term,
      row.id,
      row.machineTags.map(machineTagLabel).join('|'),
      termTypeLabel(row.type),
      row.remark,
      row.createdAt
    ].join(','))
    const blob = new Blob([`﻿${[header, ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${lexicon.name}-关键词.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    emit('notice', `词库「${lexicon.name}」关键词已导出。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '词库导出失败')
  }
}

// —— 添加关键词 ——
const showAddTermDialog = ref(false)
const termForm = ref<{
  machineTags: MachineTag[]
  type: TermType
  inputMode: 'text' | 'file'
  text: string
  remark: string
}>({ machineTags: [], type: 'NORMAL', inputMode: 'text', text: '', remark: '' })
const parsedTermCount = computed(() => termForm.value.text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).length)

function openAddTermDialog() {
  termForm.value = { machineTags: [], type: 'NORMAL', inputMode: 'text', text: '', remark: '' }
  showAddTermDialog.value = true
}

function toggleMachineTag(tag: MachineTag) {
  const index = termForm.value.machineTags.indexOf(tag)
  if (index >= 0) {
    termForm.value.machineTags.splice(index, 1)
  } else {
    termForm.value.machineTags.push(tag)
  }
}

function readTermFile(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    termForm.value.text = String(reader.result ?? '')
    input.value = ''
  }
  reader.readAsText(file)
}

async function submitTerms() {
  if (!activeLexicon.value) return
  const termList = termForm.value.text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  if (!termList.length) {
    emit('error', '请输入至少一个关键词。')
    return
  }
  if (!termForm.value.machineTags.length) {
    emit('error', '请选择至少一个机审标签。')
    return
  }
  if (termList.length > TERM_BATCH_LIMIT) {
    emit('error', `单次最多提交 ${TERM_BATCH_LIMIT} 个关键词。`)
    return
  }
  if (accountTermCount.value + termList.length > TERM_ACCOUNT_LIMIT) {
    emit('error', `账号累计关键词已达上限 ${TERM_ACCOUNT_LIMIT} 个，请先删除部分关键词。`)
    return
  }
  loading.value = true
  try {
    await addTerms({
      lexiconId: activeLexicon.value.id,
      terms: termList,
      machineTags: termForm.value.machineTags,
      type: termForm.value.type,
      remark: termForm.value.remark.trim()
    })
    showAddTermDialog.value = false
    emit('notice', `已添加 ${termList.length} 个关键词。`)
    await loadTerms()
    await refreshLexicons()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '关键词添加失败')
  } finally {
    loading.value = false
  }
}

// —— 批量 / 单个删除关键词 ——
function toggleTerm(id: number) {
  const index = selectedTermIds.value.indexOf(id)
  if (index >= 0) {
    selectedTermIds.value.splice(index, 1)
  } else {
    selectedTermIds.value.push(id)
  }
}

function toggleAllTerms() {
  if (allTermsSelected.value) {
    selectedTermIds.value = []
  } else {
    selectedTermIds.value = termPaged.paged.value.map((item) => item.id)
  }
}

async function batchDeleteTerms() {
  if (!selectedTermIds.value.length || !activeLexicon.value) return
  if (!window.confirm(`确定删除选中的 ${selectedTermIds.value.length} 个关键词吗？`)) return
  loading.value = true
  try {
    await deleteTerms(activeLexicon.value.id, selectedTermIds.value)
    emit('notice', '关键词已删除。')
    await loadTerms()
    await refreshLexicons()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '关键词删除失败')
  } finally {
    loading.value = false
  }
}

async function removeTerm(term: ReviewTerm) {
  if (!window.confirm(`确定删除关键词「${term.term}」吗？`)) return
  loading.value = true
  try {
    await deleteTerms(term.lexiconId, [term.id])
    emit('notice', '关键词已删除。')
    await loadTerms()
    await refreshLexicons()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '关键词删除失败')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refreshLexicons()
})

defineExpose({ refresh: refreshLexicons })
</script>

<template>
  <section class="panel span management-list content-review-page">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Content Review · Lexicon</p>
        <h2>词库管理</h2>
        <small>{{ mode === 'list' ? '维护敏感词库与关键词，供审核策略引用' : `词库「${activeLexicon?.name ?? ''}」关键词管理` }}</small>
      </div>
      <RrButton v-if="mode === 'detail'" @click="backToList"><template #icon><ArrowLeft :size="16" /></template>返回词库列表</RrButton>
    </div>

    <!-- 词库列表 -->
    <template v-if="mode === 'list'">
      <AdminFilterBar :loading="loading" @submit="refreshLexicons" @reset="keyword = ''; refreshLexicons()">
        <RrFormItem label="词库名称">
          <RrInput v-model:value="keyword" placeholder="输入词库名进行搜索" @keyup.enter="refreshLexicons" />
        </RrFormItem>
        <template #actions>
          <RrButton type="primary" :disabled="!canManage || loading" @click="openCreateDialog"><template #icon><Plus :size="16" /></template>新建词库</RrButton>
        </template>
      </AdminFilterBar>

            <AdminDataTable
        :columns="lexiconColumns"
        :data-source="listPaged.paged.value"
        :loading="loading"
        :current="listPaged.page.value"
        :page-size="listPaged.pageSize.value"
        :total="listPaged.total.value"
        @refresh="refreshLexicons"
        @update:current="listPaged.setPage"
        @update:page-size="(size) => (listPaged.pageSize.value = size)"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <strong>{{ record.name }}</strong>
            <small style="display:block;color:var(--rr-color-text-muted)">{{ lexiconCategoryLabel(record.category) }} · {{ record.termCount }} 个词条</small>
          </template>
          <template v-else-if="column.key === 'result'">
            <AdminStatusTag :text="lexiconResultLabel(record.result)" :tone="record.result === 'VIOLATION' ? 'danger' : (record.result === 'SUSPECTED' ? 'warning' : 'info')" />
          </template>
          <template v-else-if="column.key === 'matchMode'">
            {{ matchModeLabel(record.matchMode) }}
          </template>
          <template v-else-if="column.key === 'updatedAt'">
            {{ record.updatedAt }}
          </template>
          <template v-else-if="column.key === 'actions'">
            <RrSpace>
              <RrButton type="link" :disabled="loading" @click="openDetail(record)">管理</RrButton>
              <RrButton type="link" danger :disabled="!canManage || loading" @click="removeLexicon(record)">删除</RrButton>
              <RrButton type="link" :disabled="loading" @click="exportLexicon(record)">导出</RrButton>
            </RrSpace>
          </template>
        </template>
      </AdminDataTable>
    </template>

    <!-- 词库详情：基本信息 + 关键词管理 -->
    <template v-else-if="activeLexicon">
      <div class="content-review-cards">
        <section class="content-review-card">
          <div class="content-review-card-head">
            <h3>基本信息</h3>
            <RrButton :disabled="!canManage" @click="openEditDialog(activeLexicon)">编辑</RrButton>
          </div>
          <div class="content-review-kv">
            <span>词库名称</span><strong>{{ activeLexicon.name }}</strong>
            <span>识别结果</span><AdminStatusTag :text="lexiconResultLabel(activeLexicon.result)" :tone="activeLexicon.result === 'VIOLATION' ? 'danger' : (activeLexicon.result === 'SUSPECTED' ? 'warning' : 'info')" />
            <span>匹配模式</span><strong>{{ matchModeLabel(activeLexicon.matchMode) }}（选择后不可变更）</strong>
            <span>词库分类</span><strong>{{ lexiconCategoryLabel(activeLexicon.category) }}</strong>
            <span>最近修改时间</span><strong>{{ activeLexicon.updatedAt }}</strong>
          </div>
        </section>
        <section class="content-review-card">
          <h3>统计</h3>
          <div class="content-review-stats">
            <div><strong>{{ activeLexicon.termCount }}</strong><span>当前词库关键词数量</span></div>
            <div><strong>{{ accountTermCount }}</strong><span>账号累计关键词数量<small>上限 {{ TERM_ACCOUNT_LIMIT }} 个</small></span></div>
          </div>
        </section>
      </div>

      <AdminFilterBar :loading="loading" @submit="loadTerms" @reset="termTag = ''; termKeyword = ''; loadTerms()">
        <RrFormItem label="机审标签">
          <RrSelect v-model:value="termTag" :options="[{label: '全部机审标签', value: ''}, ...MACHINE_TAG_OPTIONS]" />
        </RrFormItem>
        <RrFormItem label="关键词检索">
          <RrInput v-model:value="termKeyword" placeholder="请输入关键词进行检索" @keyup.enter="loadTerms" />
        </RrFormItem>
        <template #actions>
          <RrButton type="primary" :disabled="!canManage || loading" @click="openAddTermDialog"><template #icon><Plus :size="16" /></template>添加关键词</RrButton>
          <RrButton type="primary" danger :disabled="!selectedTermIds.length || loading" @click="batchDeleteTerms"><template #icon><Trash2 :size="16" /></template>批量删除</RrButton>
        </template>
      </AdminFilterBar>

            <AdminDataTable
        :columns="termColumns"
        :data-source="termPaged.paged.value"
        :loading="loading"
        :current="termPaged.page.value"
        :page-size="termPaged.pageSize.value"
        :total="termPaged.total.value"
        :row-selection="{ selectedRowKeys: selectedTermIds, onChange: handleSelectionChange }"
        @refresh="loadTerms"
        @update:current="termPaged.setPage"
        @update:page-size="(size) => (termPaged.pageSize.value = size)"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'term'">
            <strong>{{ record.term }}</strong>
          </template>
          <template v-else-if="column.key === 'id'">
            {{ record.id }}
          </template>
          <template v-else-if="column.key === 'machineTags'">
            <RrSpace wrap>
              <RrTag v-for="tag in record.machineTags" :key="tag" :type="machineTagTone(tag) === 'danger' ? 'error' : (machineTagTone(tag) === 'warning' ? 'warning' : 'info')">{{ machineTagLabel(tag) }}</RrTag>
              <span v-if="!record.machineTags.length" class="muted-text">-</span>
            </RrSpace>
          </template>
          <template v-else-if="column.key === 'type'">
            {{ termTypeLabel(record.type) }}
          </template>
          <template v-else-if="column.key === 'remark'">
            {{ record.remark || '-' }}
          </template>
          <template v-else-if="column.key === 'createdAt'">
            {{ record.createdAt }}
          </template>
          <template v-else-if="column.key === 'actions'">
            <RrButton type="link" danger :disabled="!canManage || loading" @click="removeTerm(record)">删除</RrButton>
          </template>
        </template>
      </AdminDataTable>
    </template>

        <!-- 新建 / 编辑词库弹窗 -->
    <AdminDrawer
      v-model:open="showLexiconDialog"
      :title="dialogMode === 'create' ? '新建关键词库' : '编辑词库信息'"
      @update:open="showLexiconDialog = $event"
    >
      <RrForm :disabled="submitting" layout="vertical" @submit.prevent="submitLexicon">
        <RrFormItem label="词库名称" required>
          <RrInput v-model:value="lexiconForm.name" :maxlength="32" placeholder="可使用中文、英文、数字及下划线组合，32 字符内" />
        </RrFormItem>
        <RrFormItem label="识别结果">
          <RrRadioGroup v-model:value="lexiconForm.result" :options="LEXICON_RESULT_OPTIONS" />
        </RrFormItem>
        <RrFormItem label="匹配模式">
          <RrRadioGroup v-model:value="lexiconForm.matchMode" :disabled="dialogMode === 'edit'" :options="MATCH_MODE_OPTIONS" />
          <template #help>
            精确匹配：对输入的文本和关键词做直接的一致性匹配；模糊匹配：用于检测可能发生变体的词，支持拆分字、形似字、音似字、简繁体、大小写、大写数字六种形式。
          </template>
        </RrFormItem>
        <div class="drawer-actions">
          <RrSpace>
            <RrButton @click="showLexiconDialog = false" :disabled="submitting">取消</RrButton>
            <RrButton type="primary" html-type="submit" :loading="submitting" :disabled="!lexiconForm.name">{{ dialogMode === 'create' ? '确定' : '保存' }}</RrButton>
          </RrSpace>
        </div>
      </RrForm>
    </AdminDrawer>


        <!-- 添加关键词弹窗 -->
    <AdminDrawer
      v-model:open="showAddTermDialog"
      title="添加关键词"
      @update:open="showAddTermDialog = $event"
    >
      <RrForm :disabled="loading" layout="vertical" @submit.prevent="submitTerms">
        <RrFormItem label="机审标签">
          <RrSpace wrap>
            <RrButton v-for="tag in MACHINE_TAG_OPTIONS" :key="tag.value" :type="termForm.machineTags.includes(tag.value) ? 'primary' : 'default'" @click="toggleMachineTag(tag.value)">{{ tag.label }}</RrButton>
          </RrSpace>
        </RrFormItem>
        <RrFormItem label="关键词类型">
          <RrRadioGroup v-model:value="termForm.type" :options="TERM_TYPE_OPTIONS" />
        </RrFormItem>

        <RrFormItem>
          <div class="content-review-tabs" role="tablist">
            <RrButton :type="termForm.inputMode === 'text' ? 'primary' : 'default'" @click="termForm.inputMode = 'text'">输入框添加</RrButton>
            <RrButton :type="termForm.inputMode === 'file' ? 'primary' : 'default'" @click="termForm.inputMode = 'file'">文件上传</RrButton>
          </div>
        </RrFormItem>

        <RrFormItem v-if="termForm.inputMode === 'text'" label="添加关键词">
          <RrInput
            type="textarea"
            v-model:value="termForm.text"
            :rows="8"
            placeholder="请输入需要添加的关键词，每个关键词以换行来确定，单个关键词长度 20 个汉字以内，添加多个关键词以回车换行分隔，一次提交不超过 2000 个"
          />
          <template #help>已识别 {{ parsedTermCount }} 个关键词</template>
        </RrFormItem>

        <RrFormItem v-else label="上传关键词文件">
          <input type="file" accept=".txt,.csv" @change="readTermFile" />
          <template #help>输入框一次最多添加 {{ TERM_BATCH_LIMIT }} 个词。若超过该数量，则请使用文件上传的方式（txt/csv，每行一个词）。</template>
        </RrFormItem>

        <RrFormItem label="关键词备注">
          <RrInput v-model:value="termForm.remark" :maxlength="100" placeholder="请输入关键词的备注说明，最多可输入 100 个字" />
          <template #help>{{ termForm.remark.length }}/100</template>
        </RrFormItem>

        <div class="drawer-actions">
          <RrSpace>
            <RrButton @click="showAddTermDialog = false" :disabled="loading">取消</RrButton>
            <RrButton type="primary" html-type="submit" :loading="loading" :disabled="!parsedTermCount">确定</RrButton>
          </RrSpace>
        </div>
      </RrForm>
    </AdminDrawer>
  </section>
</template>
